import cx from "classnames";
import { OverviewTreeNode } from "./brandOverview.types";
import { AddEntityCard } from "./AddEntityCard";
import { OverviewEntityCard } from "./OverviewEntityCard";

type Connector = "elbow" | "through";

type EntityTreeProps = {
  tree: OverviewTreeNode[];
  selectedId: string | null;
  orderIndex: Map<string, number>;
  addLabel: string;
  showAddCard: boolean;
  onSelect: (entityId: string) => void;
  onAddEntity: () => void;
};

type SubtreeProps = {
  node: OverviewTreeNode;
  connector?: Connector;
  selectedId: string | null;
  orderIndex: Map<string, number>;
  onSelect: (entityId: string) => void;
};

const Subtree = ({
  node,
  connector,
  selectedId,
  orderIndex,
  onSelect,
}: SubtreeProps) => {
  const { entity, children } = node;

  return (
    // pl-10 shifts the whole subtree (card + its children) right, so nesting
    // compounds level over level. Connectors are drawn back into that gutter.
    <div className={cx("t-relative", { "t-pl-10": connector })}>
      {connector === "through" && (
        <span className="t-pointer-events-none t-absolute -t-bottom-4 -t-top-4 t-left-4 t-border-0 t-border-l t-border-solid t-border-neutral-20" />
      )}

      {/* card-only wrapper so the connector's top-1/2 hits the card's center */}
      <div className="t-relative">
        {connector === "elbow" && (
          <span className="t-pointer-events-none t-absolute -t-top-4 t-bottom-1/2 -t-left-6 t-w-6 t-rounded-bl-lg t-border-0 t-border-b t-border-l t-border-solid t-border-neutral-20" />
        )}
        {connector === "through" && (
          <span className="t-pointer-events-none t-absolute -t-left-6 t-top-1/2 t-w-6 t-border-0 t-border-t t-border-solid t-border-neutral-20" />
        )}

        <OverviewEntityCard
          entity={entity}
          index={orderIndex.get(entity.id) ?? 0}
          isSelected={selectedId === entity.id}
          onSelect={onSelect}
        />
      </div>

      {children.length > 0 && (
        <div className="t-mt-4 t-flex t-flex-col t-gap-4">
          {children.map((child, childIndex) => (
            <Subtree
              key={child.entity.id}
              node={child}
              connector={
                childIndex === children.length - 1 ? "elbow" : "through"
              }
              selectedId={selectedId}
              orderIndex={orderIndex}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const EntityTree = ({
  tree,
  selectedId,
  orderIndex,
  addLabel,
  showAddCard,
  onSelect,
  onAddEntity,
}: EntityTreeProps) => {
  return (
    <div className="t-flex t-flex-col t-gap-3.5">
      <div className="t-flex t-flex-col t-gap-4">
        {tree.map((node) => (
          <Subtree
            key={node.entity.id}
            node={node}
            selectedId={selectedId}
            orderIndex={orderIndex}
            onSelect={onSelect}
          />
        ))}
      </div>
      {showAddCard && <AddEntityCard label={addLabel} onClick={onAddEntity} />}
    </div>
  );
};
